import {
  ClearingSaleBuyerStatus,
  ClearingSaleBuyerStatusOrder,
} from "constants/clearingSaleAttributes";

import { caseInsensitiveCompare } from "lib/compare";

// no-redeclare rejects a same-named const+type merge, hence SortFields/SortField.
export const SortFields = {
  BUYER_NAME_ASC: "BUYER_NAME_ASC",
  BUYER_NAME_DESC: "BUYER_NAME_DESC",
  BIDDER_NUMBER_ASC: "BIDDER_NUMBER_ASC",
  BIDDER_NUMBER_DESC: "BIDDER_NUMBER_DESC",
  STATUS_UNPAID_FIRST: "STATUS_UNPAID_FIRST",
  STATUS_UNPAID_LAST: "STATUS_UNPAID_LAST",
} as const;

export type SortField = (typeof SortFields)[keyof typeof SortFields];

type BuyerId = string;
export type ValuesByBuyerId = Record<BuyerId, readonly string[] | undefined>;
type Comparator = (a: BuyerId, b: BuyerId) => number;

export type BuyerSortData = {
  buyerStatusByBuyerId: Record<BuyerId, ClearingSaleBuyerStatus>;
  bidderNumbersByBuyerId: ValuesByBuyerId;
};

// Lowest per-sale-lot value for a buyer, used as its sort key.
function getLowestValue(values: readonly string[]): string | undefined {
  let lowest: string | undefined = undefined;
  for (const value of values) {
    if (lowest === undefined || caseInsensitiveCompare(value, lowest) < 0) {
      lowest = value;
    }
  }
  return lowest;
}

// Precomputed once per sort, not inside the O(N log N) comparator.
function getLowestValueByBuyerId(
  ids: readonly BuyerId[],
  valuesByBuyerId: ValuesByBuyerId,
): Record<BuyerId, string | undefined> {
  const lowestByBuyerId: Record<BuyerId, string | undefined> = {};
  for (const id of ids) {
    lowestByBuyerId[id] = getLowestValue(valuesByBuyerId[id] ?? []);
  }
  return lowestByBuyerId;
}

// Shared mechanism only; each call site supplies its own data.
function compareByLowestValue(
  ids: readonly BuyerId[],
  valuesByBuyerId: ValuesByBuyerId,
): Comparator {
  const lowestByBuyerId = getLowestValueByBuyerId(ids, valuesByBuyerId);
  // A buyer with no sale lots has no sort key, and sorts last.
  return (a, b) => {
    const lowestA = lowestByBuyerId[a];
    const lowestB = lowestByBuyerId[b];
    if (lowestA === undefined && lowestB === undefined) {
      return 0;
    }
    if (lowestA === undefined) {
      return 1;
    }
    if (lowestB === undefined) {
      return -1;
    }
    return caseInsensitiveCompare(lowestA, lowestB);
  };
}

function reverseComparator(comparator: Comparator): Comparator {
  return (a, b) => -comparator(a, b);
}

// ids is already alphabetical (selectAlphabeticalBuyerIds); reverse it for Z-A.
function compareByReverseOfGivenOrder(ids: readonly BuyerId[]): Comparator {
  return (a, b) => ids.indexOf(b) - ids.indexOf(a);
}

// Returns null when the incoming order is already correct.
export function getSortComparator(
  sortField: SortField,
  { buyerStatusByBuyerId, bidderNumbersByBuyerId }: BuyerSortData,
  ids: readonly BuyerId[],
): Comparator | null {
  switch (sortField) {
    case SortFields.STATUS_UNPAID_FIRST:
      return (a, b) =>
        ClearingSaleBuyerStatusOrder.indexOf(buyerStatusByBuyerId[a]) -
        ClearingSaleBuyerStatusOrder.indexOf(buyerStatusByBuyerId[b]);
    case SortFields.STATUS_UNPAID_LAST:
      return reverseComparator(
        getSortComparator(
          SortFields.STATUS_UNPAID_FIRST,
          { buyerStatusByBuyerId, bidderNumbersByBuyerId },
          ids,
        ) as Comparator,
      );
    case SortFields.BIDDER_NUMBER_ASC:
      return compareByLowestValue(ids, bidderNumbersByBuyerId);
    case SortFields.BIDDER_NUMBER_DESC:
      return reverseComparator(
        compareByLowestValue(ids, bidderNumbersByBuyerId),
      );
    case SortFields.BUYER_NAME_DESC:
      return compareByReverseOfGivenOrder(ids);
    case SortFields.BUYER_NAME_ASC:
    default:
      // Already alphabetical (selectAlphabeticalBuyerIds); no re-sort needed.
      return null;
  }
}
